import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import {
  XCircle, CheckCircle2, Clock, Check, ShieldCheck, Loader2, AlertTriangle,
  FlaskConical, PackageX, Star, Pencil, Ban, Truck, History,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../../lib/format';
import { EVIDENCE_META, REJECT_REASONS } from '../../pages/stores/stores-constants';
import { GRNEditModal } from './GRNEditModal';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Inspection status pill for the GRN list ──
export function InspectionBadge({ grn }) {
  const status = grn.inspection_status || 'awaiting';
  if (status === 'inspected') {
    const d = grn.inspection_decision;
    const map = {
      all_approved: { c: 'text-emerald-700 bg-emerald-50', t: 'Approved', I: CheckCircle2 },
      partial: { c: 'text-amber-700 bg-amber-50', t: 'Partial', I: AlertTriangle },
      all_rejected: { c: 'text-rose-700 bg-rose-50', t: 'Rejected', I: XCircle },
    };
    const m = map[d] || map.partial;
    const Icon = m.I;
    return <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${m.c}`} data-testid={`grn-insp-status-${grn.id}`}><Icon className="w-3.5 h-3.5" /> {m.t}</span>;
  }
  return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full text-slate-600 bg-slate-100" data-testid={`grn-insp-status-${grn.id}`}><Clock className="w-3.5 h-3.5" /> Awaiting QC</span>;
}


const LOC_LABEL = { project: 'Project Site', office: 'Office', godown: 'Central Godown' };

export function GRNStatusBadges({ grn }) {
  return (
    <>
      {grn.is_non_po && <span className="inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold bg-violet-100 text-violet-800" data-testid={`grn-nonpo-badge-${grn.id}`}>NON-PO</span>}
      {grn.status === 'cancelled' && <span className="inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold bg-slate-200 text-slate-700" data-testid={`grn-cancelled-badge-${grn.id}`}>CANCELLED</span>}
    </>
  );
}

// ========== GRN DETAIL MODAL ==========
export function GRNDetailModal({ grn: initial, onClose, canInspect, onInspected, onChanged, currentUser }) {
  const [grn, setGrn] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setGrn(initial); }, [initial]);
  const evidence = grn.evidence || {};
  const checks = ['quantity_check', 'quality_check', 'invoice_check', 'mtc_check'];
  const lineItems = grn.items || [];
  const isCancelled = grn.status === 'cancelled';
  const isPending = !isCancelled && ['pending', 'awaiting'].includes(grn.inspection_status || 'awaiting');
  const canMutate = isPending && (currentUser?.role === 'super_admin' || currentUser?.user_id === grn.created_by_id || currentUser?.id === grn.created_by_id || currentUser?.can_approve_grn);
  const loc = grn.receiving_location_type || 'project';
  const total = lineItems.reduce((s, it) => s + Number(it.amount ?? (Number(it.received_qty || 0) * Number(it.rate || 0))), 0);

  const doCancel = async () => {
    if (cancelReason.trim().length < 3) { toast.error('Enter a reason (min 3 characters)'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/stores/grn/${grn.id}/cancel`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason: cancelReason.trim() }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Cancel failed');
      toast.success(`${grn.grn_no} cancelled`);
      setGrn(d); setCancelling(false); onChanged?.(d);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="grn-detail-modal">
        <div className="p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">{grn.grn_no} <GRNStatusBadges grn={grn} /></h2>
              <p className="text-sm text-gray-500">{grn.po_no} · {grn.vendor_name}{grn.vendor_phone ? ` · ${grn.vendor_phone}` : ''}{grn.vendor_gstin ? ` · GSTIN ${grn.vendor_gstin}` : ''}</p>
            </div>
            <div className="flex items-center gap-1">
              {canMutate && !editing && !cancelling && (<>
                <Button variant="outline" size="sm" data-testid="grn-edit-btn" onClick={() => setEditing(true)}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                <Button variant="outline" size="sm" className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="grn-cancel-btn" onClick={() => setCancelling(true)}><Ban className="w-3.5 h-3.5 mr-1" /> Cancel GRN</Button>
              </>)}
              <Button variant="ghost" onClick={onClose} data-testid="grn-detail-close"><XCircle className="w-5 h-5" /></Button>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-5">
          {isCancelled && (
            <div className="rounded-lg bg-slate-100 border border-slate-300 p-3 text-xs text-slate-700" data-testid="grn-cancelled-banner">
              <b>Cancelled</b> by {grn.cancelled_by || '—'}{grn.cancelled_at ? ` on ${formatDateTime(grn.cancelled_at)}` : ''} — {grn.cancel_reason}
            </div>
          )}
          {cancelling && (
            <div className="rounded-lg border-2 border-rose-200 bg-rose-50 p-3 space-y-2" data-testid="grn-cancel-form">
              <p className="text-xs text-rose-800 font-medium">Cancel {grn.grn_no}? The GRN number is kept, its quantity stops counting against the PO and the QC request is withdrawn.</p>
              <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={2} placeholder="Reason for cancellation *" data-testid="grn-cancel-reason" className="w-full border border-rose-200 rounded px-2 py-1.5 text-xs" />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setCancelling(false)}>Keep GRN</Button>
                <Button size="sm" className="bg-rose-600 hover:bg-rose-700" disabled={busy} onClick={doCancel} data-testid="grn-cancel-confirm">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Confirm cancel'}</Button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><span className="text-gray-500">Project</span><p className="font-medium">{grn.project_name || '—'}</p></div>
            <div><span className="text-gray-500">Receiving location</span><p className="font-medium" data-testid="grn-detail-location">{LOC_LABEL[loc] || loc}{grn.office_location_name ? ` · ${grn.office_location_name}` : ''}</p></div>
            <div><span className="text-gray-500">Challan / Bill</span><p className="font-medium">{grn.challan_no || '—'}{grn.challan_date ? ` · ${formatDate(grn.challan_date)}` : ''}</p></div>
            <div><span className="text-gray-500">Value</span><p className="font-bold" data-testid="grn-detail-total">₹{Number(grn.total_value ?? total).toLocaleString('en-IN')}</p></div>
          </div>
          <div className="grid grid-cols-3 gap-4 text-xs text-slate-600" data-testid="grn-detail-logistics">
            <div><span className="text-gray-400">Transporter</span><p>{grn.transporter_name || '—'}</p></div>
            <div><span className="text-gray-400">LR No.</span><p>{grn.lr_no || '—'}</p></div>
            <div><span className="text-gray-400">Vehicle</span><p>{grn.vehicle_no || (grn.delivery_entries?.length ? `${grn.delivery_entries.length} vehicles` : '—')}</p></div>
          </div>
          {lineItems.length > 0 && (
            <div data-testid="grn-detail-items">
              <h3 className="text-sm font-semibold mb-2">Line Items</h3>
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50"><tr>
                    <th className="text-left px-3 py-2">Description</th>
                    <th className="text-right px-3 py-2">Qty</th>
                    <th className="text-left px-3 py-2">Unit</th>
                    <th className="text-right px-3 py-2">Rate ₹</th>
                    <th className="text-right px-3 py-2">Amount ₹</th>
                    <th className="text-left px-3 py-2">Bin</th>
                    <th className="text-left px-3 py-2">Mfg. Date</th>
                  </tr></thead>
                  <tbody className="divide-y">
                    {lineItems.map((it, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2">{it.description || it.item_code || '—'}{it.item_code && <span className="block text-[10px] text-slate-400 font-mono">{it.item_code}</span>}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{it.received_qty}</td>
                        <td className="px-3 py-2">{it.unit || '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums" data-testid={`grn-rate-${i}`}>{Number(it.rate || 0).toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium" data-testid={`grn-amount-${i}`}>{Number(it.amount ?? Number(it.received_qty || 0) * Number(it.rate || 0)).toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2 font-mono text-slate-600 text-[11px]" data-testid={`grn-bin-${i}`}>{it.bin_location || <span className="text-slate-300">—</span>}</td>
                        <td className="px-3 py-2 text-[11px]" data-testid={`grn-mfg-date-${i}`}>{it.manufacturing_date ? formatDate(it.manufacturing_date) : <span className="text-slate-300">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="bg-gray-50 font-semibold"><td colSpan={4} className="px-3 py-2 text-right">Total</td><td className="px-3 py-2 text-right tabular-nums">₹{total.toLocaleString('en-IN')}</td><td colSpan={2} /></tr></tfoot>
                </table>
              </div>
            </div>
          )}
          {grn.delivery_entries?.length > 0 && (
            <div data-testid="grn-detail-vehicles">
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Truck className="w-4 h-4" /> Vehicles ({grn.delivery_entries.length})</h3>
              <div className="space-y-2">
                {grn.delivery_entries.map((en, ei) => (
                  <div key={ei} className="border rounded-lg p-3" data-testid={`grn-vehicle-${ei}`}>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mb-2">
                      <span className="font-semibold">Vehicle {ei + 1}: {en.vehicle_no}</span>
                      {en.challan_no && <span className="text-slate-500">Challan {en.challan_no}{en.challan_date ? ` · ${formatDate(en.challan_date)}` : ''}</span>}
                      {en.driver_name && <span className="text-slate-500">Driver {en.driver_name}</span>}
                      {en.lr_no && <span className="text-slate-500">LR {en.lr_no}</span>}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {['quantity_check', 'quality_check', 'mtc_check'].map(k => {
                        const ev = en.evidence?.[k];
                        return (
                          <div key={k} className="rounded border p-1.5">
                            <div className="text-[10px] text-gray-600 mb-1">{EVIDENCE_META[k].label}</div>
                            {ev?.url ? <a href={`${API}${ev.url}`} target="_blank" rel="noreferrer"><img src={`${API}${ev.url}`} alt={k} className="w-full h-20 object-cover rounded" /></a>
                              : <div className="h-20 flex items-center justify-center text-[10px] text-gray-400 bg-gray-50 rounded">Not captured</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <h3 className="text-sm font-semibold mb-2">Receipt Evidence</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {checks.map(key => {
                const ev = evidence[key];
                return (
                  <div key={key} className="rounded-lg border p-2" data-testid={`grn-detail-evidence-${key}`}>
                    <div className="text-[11px] font-medium text-gray-600 mb-1">{EVIDENCE_META[key].label}</div>
                    {ev?.url ? (
                      <a href={`${API}${ev.url}`} target="_blank" rel="noreferrer">
                        <img src={`${API}${ev.url}`} alt={EVIDENCE_META[key].label} className="w-full h-24 object-cover rounded hover:opacity-90" />
                      </a>
                    ) : (
                      <div className="h-24 flex items-center justify-center text-[10px] text-gray-400 bg-gray-50 rounded">Not captured</div>
                    )}
                    {ev && (
                      <div className="mt-1.5">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${ev.checked ? 'text-emerald-700' : 'text-amber-700'}`}>
                          {ev.checked ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                          {ev.checked ? 'Verified OK' : 'Not verified'}
                        </span>
                        {ev.remarks && <p className="text-[10px] text-gray-500 mt-0.5 italic break-words">"{ev.remarks}"</p>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {!isCancelled && <GRNInspectionPanel grn={grn} canInspect={canInspect} onInspected={onInspected} />}
          {!isCancelled && <VendorRatingPanel grn={grn} />}
          {grn.edit_history?.length > 0 && (
            <div className="border rounded-lg p-3" data-testid="grn-edit-history">
              <h3 className="text-xs font-semibold mb-2 flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Edit history</h3>
              <ul className="space-y-1.5 text-[11px] text-slate-600">
                {grn.edit_history.map((h, i) => (
                  <li key={i}>
                    <span className="font-medium">{h.by || '—'}</span> · {formatDateTime(h.at)}
                    {h.action === 'cancelled' ? <span> — cancelled: {h.reason}</span>
                      : <span> — changed {Object.keys(h.after || {}).join(', ') || '—'}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      {editing && <GRNEditModal grn={grn} onClose={() => setEditing(false)} onSaved={(g) => { setGrn(g); setEditing(false); onChanged?.(g); }} />}
    </div>
  );
}

// ── Vendor Rating Panel ──
function VendorRatingPanel({ grn }) {
  const [rating, setRating] = useState(grn.vendor_rating || 0);
  const [hover, setHover] = useState(0);
  const [notes, setNotes] = useState(grn.vendor_rating_notes || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(!!grn.vendor_rating);
  const STAR_LABELS = ["", "Poor", "Below average", "Average", "Good", "Excellent"];

  const submitRating = async () => {
    if (!rating) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/stores/grn/${grn.id}/rate`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating, notes: notes.trim() || null }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Failed");
      toast.success("Vendor rating saved");
      setSaved(true);
    } catch (e) { toast.error(e.message || "Could not save rating"); } finally { setSaving(false); }
  };

  return (
    <div className="border rounded-lg p-4 bg-slate-50/60" data-testid="grn-vendor-rating-panel">
      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
        <Star className="w-4 h-4 text-amber-500" /> Vendor Performance Rating
        {saved && grn.vendor_rated_by && <span className="text-[10px] text-slate-500 font-normal ml-2">Last rated by {grn.vendor_rated_by}</span>}
      </h3>
      <div className="flex items-center gap-1 mb-3" data-testid="grn-star-picker">
        {[1, 2, 3, 4, 5].map(n => (
          <button key={n} onClick={() => setRating(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} data-testid={`grn-star-${n}`} className="p-0.5">
            <Star className={`w-7 h-7 transition-colors ${n <= (hover || rating) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
          </button>
        ))}
        {(hover || rating) > 0 && <span className="ml-2 text-sm font-medium text-amber-700">{STAR_LABELS[hover || rating]}</span>}
      </div>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
        placeholder="Optional notes (delivery timeliness, quality, packaging…)"
        className="w-full border border-slate-200 rounded px-3 py-2 text-sm" data-testid="grn-rating-notes" />
      <div className="flex justify-end mt-2">
        <Button size="sm" onClick={submitRating} disabled={!rating || saving} data-testid="grn-rating-submit" className="bg-[#3b4872] hover:bg-[#2c3658]">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
          {saved ? "Update Rating" : "Save Rating"}
        </Button>
      </div>
    </div>
  );
}

// ── Quality Inspection Panel ──
export function GRNInspectionPanel({ grn, canInspect, onInspected }) {
  if (grn.inspection_status === 'inspected') return <InspectedSummary grn={grn} />;
  return (
    <div className="rounded-lg border-2 border-amber-200 bg-amber-50/50 p-4" data-testid="grn-inspection-panel">
      <div className="flex items-center gap-2 mb-1">
        <FlaskConical className="w-4 h-4 text-amber-700" />
        <h3 className="text-sm font-bold text-amber-900">Quality Inspection Required</h3>
      </div>
      <p className="text-xs text-amber-800 mb-3">Material has been received but is <b>NOT yet in stock</b>. A Quality user must inspect and release approved quantities. Rejected quantities are returned to the vendor.</p>
      {canInspect
        ? <InspectionForm grn={grn} onInspected={onInspected} />
        : <p className="text-xs text-slate-600 italic">You don't have Quality Inspection permission — awaiting a Quality team member.</p>}
    </div>
  );
}

function InspectedSummary({ grn }) {
  const lines = grn.items || [];
  const decMap = { all_approved: 'All Approved', partial: 'Partially Approved', all_rejected: 'All Rejected' };
  return (
    <div className="rounded-lg border p-4" data-testid="grn-inspected-summary">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold flex items-center gap-2"><FlaskConical className="w-4 h-4" /> Inspection Result</h3>
        <InspectionBadge grn={grn} />
      </div>
      <p className="text-xs text-gray-500 mb-3">{decMap[grn.inspection_decision] || '—'} · by {grn.inspected_by || '—'}{grn.inspected_at ? ` · ${formatDateTime(grn.inspected_at)}` : ''}</p>
      <table className="w-full text-xs">
        <thead className="text-gray-500"><tr><th className="text-left py-1">Item</th><th className="text-right">Received</th><th className="text-right text-emerald-700">Approved</th><th className="text-right text-rose-700">Rejected</th></tr></thead>
        <tbody>{lines.map((l, i) => (
          <tr key={i} className="border-t align-top">
            <td className="py-1">{l.description}</td>
            <td className="text-right">{l.received_qty}</td>
            <td className="text-right text-emerald-700 font-medium">{l.approved_qty ?? '—'}</td>
            <td className="text-right text-rose-700 font-medium">{l.rejected_qty || 0}{l.rejection_reasons?.length ? <span className="block text-[10px] text-gray-400 font-normal">{l.rejection_reasons.join(', ')}</span> : null}</td>
          </tr>
        ))}</tbody>
      </table>
      {grn.vendor_change_request_id && <p className="mt-2 text-[11px] text-rose-700 flex items-center gap-1"><PackageX className="w-3.5 h-3.5" /> Vendor change request raised — see the "Vendor Requests" tab.</p>}
    </div>
  );
}

export function InspectionForm({ grn, onInspected }) {
  const lines = grn.items || [];
  const [decision, setDecision] = useState('all_approved');
  const [rows, setRows] = useState(() => lines.map(l => ({
    boq_id: l.boq_id, description: l.description, unit: l.unit,
    received: Number(l.received_qty || 0), approved: Number(l.received_qty || 0), rejected: 0, reasons: '',
  })));
  const [replacement, setReplacement] = useState(true);
  const [newInvoice, setNewInvoice] = useState(true);
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setRows(rs => rs.map(r => {
      if (decision === 'all_approved') return { ...r, approved: r.received, rejected: 0, reasons: '' };
      if (decision === 'all_rejected') return { ...r, approved: 0, rejected: r.received };
      return r;
    }));
  }, [decision]);

  const setApproved = (idx, val) => {
    const v = Math.max(0, Number(val) || 0);
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, approved: Math.min(v, r.received), rejected: Math.max(0, +(r.received - Math.min(v, r.received)).toFixed(4)) } : r));
  };
  const setReason = (idx, val) => setRows(rs => rs.map((r, i) => i === idx ? { ...r, reasons: val } : r));

  const submit = async () => {
    if (decision === 'partial') {
      for (const r of rows) {
        if (Math.abs((r.approved + r.rejected) - r.received) > 0.01) { toast.error(`${r.description}: approved + rejected must equal received (${r.received})`); return; }
        if (r.rejected > 0 && !r.reasons.trim()) { toast.error(`${r.description}: a rejection reason is required`); return; }
      }
    }
    setSubmitting(true);
    try {
      const body = {
        decision,
        items: rows.map(r => ({ boq_id: r.boq_id, approved_qty: r.approved, rejected_qty: r.rejected, reasons: r.reasons.split(',').map(s => s.trim()).filter(Boolean) })),
        replacement_requested: decision !== 'all_approved' ? replacement : false,
        new_invoice_expected: decision !== 'all_approved' ? newInvoice : false,
        remarks,
      };
      const res = await fetch(`${API}/api/stores/grn/${grn.id}/inspection`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const detail = errData.detail;
        const msg = typeof detail === 'string' ? detail
          : (detail?.message || detail?.msg || JSON.stringify(detail) || 'Inspection failed');
        throw new Error(msg);
      }
      const data = await res.json();
      const gotStock = (data.inspection?.items || []).some(it => (it.approved_qty || 0) > 0 && (it.boq_id || it.material_id));
      toast.success(
        data.vendor_change_request_id
          ? 'Inspection recorded · vendor change request raised'
          : gotStock
            ? 'Inspection recorded · approved qty released to stock'
            : 'Inspection recorded'
      );
      onInspected?.();
    } catch (e) {
      toast.error(typeof e.message === 'string' ? e.message : 'Inspection failed — please try again');
      console.error('[GRN Inspection] error:', e);
    } finally { setSubmitting(false); }
  };

  const editable = decision === 'partial';
  return (
    <div className="space-y-3" data-testid="grn-inspection-form">
      <div className="flex flex-wrap gap-2">
        {[['all_approved', 'All Approved', CheckCircle2], ['partial', 'Partial', AlertTriangle], ['all_rejected', 'All Rejected', XCircle]].map(([key, label, Icon]) => (
          <button key={key} type="button" onClick={() => setDecision(key)} data-testid={`insp-decision-${key}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${decision === key ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300 hover:border-[#3b4872]'}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>
      <div className="rounded-lg border bg-white overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left px-2 py-1.5">Item</th>
            <th className="text-right px-2">Received</th>
            <th className="text-right px-2 text-emerald-700">Approved</th>
            <th className="text-right px-2 text-rose-700">Rejected</th>
            {editable && <th className="text-left px-2">Reason</th>}
          </tr></thead>
          <tbody>{rows.map((r, idx) => (
            <tr key={r.boq_id} className="border-t" data-testid={`insp-line-${idx}`}>
              <td className="px-2 py-1.5">{r.description}<span className="text-slate-400"> ({r.unit})</span></td>
              <td className="px-2 text-right">{r.received}</td>
              <td className="px-2 text-right">
                {editable ? (
                  <input type="number" min={0} max={r.received} value={r.approved} onChange={e => setApproved(idx, e.target.value)}
                    data-testid={`insp-approved-${idx}`} className="w-16 px-1.5 py-0.5 border border-slate-300 rounded text-right text-xs" />
                ) : <span className="text-emerald-700 font-medium">{r.approved}</span>}
              </td>
              <td className="px-2 text-right text-rose-700 font-medium">{r.rejected}</td>
              {editable && (
                <td className="px-2">
                  <input list="insp-reasons" value={r.reasons} onChange={e => setReason(idx, e.target.value)} disabled={r.rejected <= 0}
                    placeholder={r.rejected > 0 ? 'Reason…' : '—'} data-testid={`insp-reason-${idx}`}
                    className="w-36 px-1.5 py-0.5 border border-slate-300 rounded text-xs disabled:bg-slate-50 disabled:text-slate-300" />
                </td>
              )}
            </tr>
          ))}</tbody>
        </table>
        <datalist id="insp-reasons">{REJECT_REASONS.map(r => <option key={r} value={r} />)}</datalist>
      </div>
      {decision !== 'all_approved' && (
        <div className="flex flex-wrap gap-4 text-xs">
          <label className="flex items-center gap-1.5 cursor-pointer" data-testid="insp-replacement-toggle">
            <input type="checkbox" checked={replacement} onChange={e => setReplacement(e.target.checked)} /> Replacement requested from vendor
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer" data-testid="insp-invoice-toggle">
            <input type="checkbox" checked={newInvoice} onChange={e => setNewInvoice(e.target.checked)} /> New / revised invoice expected
          </label>
        </div>
      )}
      <textarea value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Inspection remarks (optional)…"
        data-testid="insp-remarks" rows={2} className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs" />
      <Button onClick={submit} disabled={submitting} data-testid="insp-submit-btn" className="w-full">
        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
        Record Inspection & Release Approved Qty
      </Button>
    </div>
  );
}
